import { Box, Checkbox, Typography } from '@material-ui/core';
import EmailIcon from '@eduzz/houston-icons/EmailOutline';
import MobileIcon from '@eduzz/houston-icons/NotificationSolid';
import SaveIcon from '@material-ui/icons/Save';
import CenteredPage from 'components/Shared/CenteredPage';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@eduzz/houston-ui/Button';
import { useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { getNotificationConfig, saveNotificationConfig } from 'services/notification';
import { UnsubscribedProducerList } from './UnsubscribedProducerList';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    '& p,b': {
      color: theme.palette.common.white,
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: theme.spacing(8),
  },
  header: {
    marginTop: theme.spacing(12),
    padding: theme.spacing(2, 0),
    paddingRight: theme.spacing(8),
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.common.white}`,
    '& span': {
      color: theme.palette.common.white,
      padding: 0,
      marginRight: theme.spacing(1),
    },
  },
  headerCheckbox: {
    display: 'flex',
    width: 'fit-content',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconHeader: {
    display: 'flex',
    flexDirection: 'row',
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4, 0),
    paddingRight: theme.spacing(8),
    borderBottom: `1px solid ${theme.palette.grey[600]}`,
  },
  itemDescription: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
  },
  itemCheckbox: {
    display: 'flex',
    flexDirection: 'row',
  },
  checkbox: {
    color: theme.palette.common.white,
  },
}));

const Notification = () => {
  const [config, setConfig] = useState({
    comment: { email: true, push: true },
    newContent: { email: true, push: true },
    newCourseTrack: { email: true, push: true },
    sendEmailToStudents: { email: true, push: true },
  });
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      toggleAll: false,
      ...config,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      formik.setSubmitting(true);
      saveNotificationConfig(values).finally(() => formik.setSubmitting(false));
    },
  });
  const itemMapper = useRef([
    {
      key: 'newContent',
      title: 'Novo conteúdo',
      description: 'Receba todas as novidades de conteúdos referente ao seu curso.',
    },
    {
      key: 'newCourseTrack',
      title: 'Novo curso no pacote',
      description: 'Receba todas as novidades de cursos referente ao seu pacote.',
    },
    {
      key: 'comment',
      title: 'Resposta de comentário',
      description: 'Receba todas as notificações referente aos seus comentários.',
    },
    {
      key: 'sendEmailToStudents',
      title: 'E-mail dos produtores',
      description: 'Receba e-mail de promoções ou comunicados dos produtores.',
      disablePush: true,
    },
  ]);

  const toggleCheckbox = (key: string, newValue: object) => () => {
    const currentValue = formik.values[key];
    formik.setFieldValue(key, { ...currentValue, ...newValue });
  };

  const toggleAllConfigs = () => {
    const newState = !formik.values.toggleAll;
    formik.setFieldValue('toggleAll', newState);
    itemMapper.current.forEach(({ key }) => {
      formik.setFieldValue(key, { email: newState, push: newState });
    });
  };

  useEffect(() => {
    setLoading(true);
    getNotificationConfig()
      .then(setConfig)
      .finally(() => setLoading(false));
  }, []);

  return (
    <CenteredPage paddingTop={12} className={classes.root}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant='h4' component='b'>
          Notificações
        </Typography>
        <div className={classes.header}>
          <div className={classes.headerCheckbox}>
            <Checkbox checked={formik.values.toggleAll} onClick={toggleAllConfigs} />
            <Typography variant='body1' component='b'>
              Ativar todos
            </Typography>
          </div>
          <div className={classes.iconHeader}>
            <Box width={40} marginRight={4}>
              <EmailIcon size={24} />
              <Typography variant='caption'>E-mail</Typography>
            </Box>
            <Box width={40}>
              <MobileIcon size={24} />
              <Typography variant='caption'>Push</Typography>
            </Box>
          </div>
        </div>

        {itemMapper.current.map((item) => (
          <div className={classes.item}>
            <div className={classes.itemDescription}>
              <Typography variant='body1' component='b'>
                {item.title}
              </Typography>
              <Box marginTop={1}>
                <Typography variant='body1'>{item.description}</Typography>
              </Box>
            </div>
            <div className={classes.itemCheckbox}>
              <Box width={40} marginRight={4}>
                <Checkbox
                  disabled={loading}
                  onClick={toggleCheckbox(item.key, { email: !formik?.values[item.key]?.email })}
                  checked={formik.values[item.key]?.email}
                  classes={{ root: classes.checkbox }}
                  color='primary'
                />
              </Box>
              <Box width={40}>
                {!item.disablePush && (
                  <Checkbox
                    disabled={loading}
                    onClick={toggleCheckbox(item.key, { push: !formik?.values[item.key]?.push })}
                    checked={formik.values[item.key]?.push}
                    classes={{ root: classes.checkbox }}
                    color='primary'
                  />
                )}
              </Box>
            </div>
          </div>
        ))}

        <div className={classes.footer}>
          <Button disabled={formik.isSubmitting} type='submit' startIcon={<SaveIcon />}>
            Salvar
          </Button>
          <Box marginRight={6}>
            <Button disabled={formik.isSubmitting} onClick={formik.handleReset} variant='text'>
              Descartar alterações
            </Button>
          </Box>
        </div>
      </form>
      <UnsubscribedProducerList />
    </CenteredPage>
  );
};

export default Notification;
